import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
} from '@mui/material';
import { MdExpandMore, MdPublic, MdSearch } from 'react-icons/md';
import { useApmcPickerOptions } from '@features/apmcs/hooks';
import type { ApmcPickerOption } from '@features/apmcs/schema';

interface Props {
  value: string[];
  onChange: (apmcIds: string[]) => void;
  error?: string;
}

interface StateGroup {
  stateId: string;
  stateName: string;
  apmcs: ApmcPickerOption[];
}

/**
 * Custom multi-select picker for closure APMC scope.
 *
 * Three apply-modes the editor can mix freely:
 *   1. "All India" button — selects every active APMC
 *   2. Per-state select-all / clear-state — selects/deselects every APMC in a state
 *   3. Individual APMC checkboxes — fine-grained
 *
 * APMCs grouped by state in collapsible accordions (closed by default for
 * scroll-friendly initial state). Search filters both states and APMCs.
 */
export function ApmcScopePicker({ value, onChange, error }: Props) {
  const { data: options = [], isLoading, error: queryError } = useApmcPickerOptions();
  const [search, setSearch] = useState('');

  // Group flat options into per-state buckets. Sort already handled server-side
  // (state name → district name → apmc name), so insertion order is stable.
  const groups = useMemo<StateGroup[]>(() => {
    const byState = new Map<string, StateGroup>();
    for (const opt of options) {
      const key = opt.state.id;
      const existing = byState.get(key);
      if (existing) {
        existing.apmcs.push(opt);
      } else {
        byState.set(key, {
          stateId: opt.state.id,
          stateName: opt.state.name,
          apmcs: [opt],
        });
      }
    }
    return Array.from(byState.values());
  }, [options]);

  const allIds = useMemo(() => options.map((o) => o.id), [options]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredGroups = useMemo<StateGroup[]>(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map((g) => {
        const stateMatches = g.stateName.toLowerCase().includes(term);
        const matchingApmcs = stateMatches
          ? g.apmcs
          : g.apmcs.filter(
              (a) =>
                a.name.toLowerCase().includes(term) ||
                a.district.name.toLowerCase().includes(term),
            );
        return matchingApmcs.length > 0
          ? { ...g, apmcs: matchingApmcs }
          : null;
      })
      .filter((g): g is StateGroup => g !== null);
  }, [groups, search]);

  // User-controlled expansion state. Accordions are always fully controlled
  // (boolean expanded prop, no controlled/uncontrolled switching).
  const [userExpanded, setUserExpanded] = useState<Set<string>>(new Set());
  const isSearching = search.trim().length > 0;

  // When the editor is searching, expansion is overridden — every matching
  // state group opens automatically so hits are visible without a click. When
  // search clears, the editor's manually-toggled expansion state takes over.
  const effectivelyExpanded = useMemo<Set<string>>(() => {
    if (isSearching) return new Set(filteredGroups.map((g) => g.stateId));
    return userExpanded;
  }, [isSearching, filteredGroups, userExpanded]);

  const toggleStateExpansion = (stateId: string, expanded: boolean) => {
    if (isSearching) return; // expansion is search-driven; click is a no-op
    setUserExpanded((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(stateId);
      else next.delete(stateId);
      return next;
    });
  };

  const selectAllInState = (group: StateGroup) => {
    const stateIds = new Set(group.apmcs.map((a) => a.id));
    const merged = new Set(value);
    for (const id of stateIds) merged.add(id);
    onChange(Array.from(merged));
  };

  const clearState = (group: StateGroup) => {
    const stateIds = new Set(group.apmcs.map((a) => a.id));
    onChange(value.filter((id) => !stateIds.has(id)));
  };

  const toggleApmc = (apmcId: string) => {
    if (selectedSet.has(apmcId)) {
      onChange(value.filter((id) => id !== apmcId));
    } else {
      onChange([...value, apmcId]);
    }
  };

  const selectAllIndia = () => onChange(allIds);
  const clearAll = () => onChange([]);

  if (isLoading) {
    return (
      <Box className="flex items-center justify-center py-6">
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (queryError) {
    return (
      <div className="rounded-lg border border-danger/30 bg-danger/[0.05] p-3 text-[12.5px] text-danger">
        Failed to load APMC options. Refresh and try again.
      </div>
    );
  }

  const selectedCount = value.length;
  const totalCount = options.length;

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-[12.5px] font-medium text-foreground">
          APMCs affected{' '}
          <span className="text-foreground-muted">
            ({selectedCount} of {totalCount})
          </span>
        </label>
        <div className="flex gap-1">
          <Button
            size="small"
            variant={selectedCount === totalCount ? 'contained' : 'outlined'}
            startIcon={<MdPublic />}
            onClick={selectAllIndia}
            disabled={totalCount === 0}
            sx={{ textTransform: 'none', fontSize: '12px', py: 0.5 }}
          >
            All India
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={clearAll}
            disabled={selectedCount === 0}
            sx={{ textTransform: 'none', fontSize: '12px', py: 0.5 }}
          >
            Clear
          </Button>
        </div>
      </div>

      <TextField
        size="small"
        placeholder="Search APMC, district, or state…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <MdSearch size={16} className="mr-1 text-foreground-muted" />
            ),
          },
        }}
      />

      <div className="max-h-[360px] overflow-y-auto rounded-lg border border-border">
        {filteredGroups.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12.5px] text-foreground-muted">
            No APMCs match "{search}".
          </div>
        ) : (
          filteredGroups.map((group) => {
            const stateSelected = group.apmcs.filter((a) =>
              selectedSet.has(a.id),
            ).length;
            const allInStateSelected =
              stateSelected === group.apmcs.length && group.apmcs.length > 0;
            const someInStateSelected =
              stateSelected > 0 && stateSelected < group.apmcs.length;

            return (
              <Accordion
                key={group.stateId}
                disableGutters
                square
                expanded={effectivelyExpanded.has(group.stateId)}
                onChange={(_, isExpanded) =>
                  toggleStateExpansion(group.stateId, isExpanded)
                }
                sx={{
                  '&:before': { display: 'none' },
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  boxShadow: 'none',
                  bgcolor: 'transparent',
                }}
              >
                <AccordionSummary
                  expandIcon={<MdExpandMore />}
                  sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                >
                  <div className="flex w-full items-center justify-between pr-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-foreground">
                        {group.stateName}
                      </span>
                      <span className="text-[11px] text-foreground-muted">
                        {stateSelected}/{group.apmcs.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (allInStateSelected) {
                          clearState(group);
                        } else {
                          selectAllInState(group);
                        }
                      }}
                      className="rounded px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
                    >
                      {allInStateSelected
                        ? 'Clear all'
                        : someInStateSelected
                          ? 'Select rest'
                          : 'Select all'}
                    </button>
                  </div>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                  <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                    {group.apmcs.map((apmc) => (
                      <label
                        key={apmc.id}
                        className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 hover:bg-surface-muted"
                      >
                        <Checkbox
                          size="small"
                          checked={selectedSet.has(apmc.id)}
                          onChange={() => toggleApmc(apmc.id)}
                          sx={{ p: 0.5 }}
                        />
                        <span className="text-[12.5px] text-foreground">
                          {apmc.name}
                          <span className="ml-1 text-[11px] text-foreground-muted">
                            · {apmc.district.name}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </div>

      {error && (
        <div className="text-[12px] text-danger">{error}</div>
      )}
    </div>
  );
}
